import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FiFileText, FiTrash2, FiVolume2 } from "react-icons/fi";
import {
  BulkPermanentlyDeletePhonemesDocument,
  BulkPermanentlyDeletePhonicsAudioDocument,
  BulkRestorePhonemesDocument,
  BulkRestorePhonicsAudioDocument,
  GetPhonemesGroupedDocument,
  GetPhonicsAudiosByPhonemeDocument,
  GetTrashedPhonemesDocument,
  GetTrashedPhonicsAudioDocument,
  PermanentlyDeletePhonemeDocument,
  PermanentlyDeletePhonicsAudioDocument,
  RestorePhonemeDocument,
  RestorePhonicsAudioDocument,
} from "../../../generated/graphql";
import { ConfirmationModal } from "../modals";
import { EmptyState, TrashHeader, TrashTable } from "../shared";
import type { TrashTableColumn } from "../shared";
import { PHONEME_TYPE_LABELS } from "./constants";

const columns: TrashTableColumn[] = [
  { header: "Type" },
  { header: "Phoneme" },
  { header: "Category" },
  { header: "Details" },
  { header: "Deleted At" },
];

// unified trash item type
type TrashItem = {
  id: string;
  itemType: "phoneme" | "audio";
  phoneme: string;
  phonemeType: string;
  deletedAt: number | null | undefined;
  // for phoneme items
  tags?: string[];
  // for audio items
  voiceVariant?: number;
  filename?: string;
};

export function PhonicsTrashPage() {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState<TrashItem[]>([]);
  const [isDeletingPermanently, setIsDeletingPermanently] = useState(false);

  // query for trashed phonemes
  const { data: phonemesData, loading: phonemesLoading } = useQuery(GetTrashedPhonemesDocument);

  // query for trashed phonics audio
  const { data: audioData, loading: audioLoading } = useQuery(GetTrashedPhonicsAudioDocument);

  const [restorePhoneme] = useMutation(RestorePhonemeDocument, {
    refetchQueries: [
      { query: GetTrashedPhonemesDocument },
      // phoneme type null in variables, to match apollo normalization with the root query ( avoid re render )
      { query: GetPhonemesGroupedDocument, variables: { phonemeType: null } },
    ],
    onCompleted: () => {
      toast.success("Phoneme restored successfully (including all audio files)");
    },
    onError: (error) => {
      toast.error(`Failed to restore: ${error.message}`);
    },
  });

  const [restorePhonicsAudio] = useMutation(RestorePhonicsAudioDocument, {
    refetchQueries: (result) => [
      { query: GetTrashedPhonicsAudioDocument },
      // phoneme type null in variables, to match apollo normalization with the root query ( avoid re render )
      { query: GetPhonemesGroupedDocument, variables: { phonemeType: null } },
      // refetch phonics audio for the specific phoneme in VoicesModal
      {
        query: GetPhonicsAudiosByPhonemeDocument,
        variables: { phonemeId: result.data?.restorePhonicsAudio?.phonemeId },
      },
    ],
    onCompleted: () => {
      toast.success("Audio file restored successfully");
    },
    onError: (error) => {
      toast.error(`Failed to restore: ${error.message}`);
    },
  });

  const [bulkRestorePhonemes] = useMutation(BulkRestorePhonemesDocument, {
    refetchQueries: [
      { query: GetTrashedPhonemesDocument },
      // phoneme type null in variables, to match apollo normalization with the root query ( avoid re render )
      { query: GetPhonemesGroupedDocument, variables: { phonemeType: null } },
    ],
    onCompleted: () => {
      toast.success("Phonemes restored successfully");
    },
    onError: (error) => {
      toast.error(`Failed to restore: ${error.message}`);
    },
  });

  const [bulkRestorePhonicsAudio] = useMutation(BulkRestorePhonicsAudioDocument, {
    refetchQueries: [
      { query: GetTrashedPhonicsAudioDocument },
      { query: GetPhonemesGroupedDocument, variables: { phonemeType: null } },
    ],
    update: (cache) => {
      // evict all cached phonicsAudiosByPhoneme queries since we don't know which phonemes were affected
      cache.evict({ fieldName: "phonicsAudiosByPhoneme" });
      cache.gc();
    },
    onCompleted: () => {
      toast.success("Audio files restored successfully");
    },
    onError: (error) => {
      toast.error(`Failed to restore: ${error.message}`);
    },
  });

  const [permanentlyDeletePhoneme] = useMutation(PermanentlyDeletePhonemeDocument, {
    refetchQueries: [{ query: GetTrashedPhonemesDocument }],
    onCompleted: () => {
      toast.success("Phoneme permanently deleted");
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const [permanentlyDeletePhonicsAudio] = useMutation(PermanentlyDeletePhonicsAudioDocument, {
    refetchQueries: [{ query: GetTrashedPhonicsAudioDocument }],
    onCompleted: () => {
      toast.success("Audio file permanently deleted");
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const [bulkPermanentlyDeletePhonemes] = useMutation(BulkPermanentlyDeletePhonemesDocument, {
    refetchQueries: [{ query: GetTrashedPhonemesDocument }],
    onCompleted: () => {
      toast.success("Phonemes permanently deleted");
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const [bulkPermanentlyDeletePhonicsAudio] = useMutation(
    BulkPermanentlyDeletePhonicsAudioDocument,
    {
      refetchQueries: [{ query: GetTrashedPhonicsAudioDocument }],
      onCompleted: () => {
        toast.success("Audio files permanently deleted");
      },
      onError: (error) => {
        toast.error(`Failed to delete: ${error.message}`);
      },
    }
  );

  // combine trashed phonemes and audio into unified list
  const trashItems = useMemo(() => {
    const items: TrashItem[] = [];

    const phonemes = phonemesData?.trashedPhonemes?.edges?.map((e) => e.node) || [];
    for (const phoneme of phonemes) {
      items.push({
        id: `phoneme:${phoneme.id}`,
        itemType: "phoneme",
        phoneme: phoneme.phoneme,
        phonemeType: phoneme.phonemeType,
        tags: phoneme.tags,
        deletedAt: phoneme.deletedAt,
      });
    }

    const audios = audioData?.trashedPhonicsAudio?.edges?.map((e) => e.node) || [];
    for (const audio of audios) {
      items.push({
        id: `audio:${audio.id}`,
        itemType: "audio",
        phoneme: audio.phoneme.phoneme,
        phonemeType: audio.phoneme.phonemeType,
        voiceVariant: audio.voiceVariant,
        filename: audio.filename,
        deletedAt: audio.deletedAt,
      });
    }

    // Sort by deletedAt (most recent first)
    items.sort((a, b) => {
      const aTime = a.deletedAt || 0;
      const bTime = b.deletedAt || 0;
      return bTime - aTime;
    });

    return items;
  }, [phonemesData, audioData]);

  const loading = phonemesLoading || audioLoading;

  const handleToggleSelect = (id: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === trashItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(trashItems.map((item) => item.id)));
    }
  };

  const handleRestore = async (id: string) => {
    const item = trashItems.find((i) => i.id === id);
    if (!item) return;

    const actualId = id.split(":")[1];
    if (!actualId) return;

    if (item.itemType === "phoneme") {
      await restorePhoneme({ variables: { id: actualId } });
    } else {
      await restorePhonicsAudio({ variables: { id: actualId } });
    }

    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const handlePermanentDelete = (id: string) => {
    const item = trashItems.find((i) => i.id === id);
    if (!item) return;

    setItemsToDelete([item]);
    setShowDeleteConfirm(true);
  };

  const handleConfirmPermanentDelete = async () => {
    if (itemsToDelete.length === 0) return;

    setIsDeletingPermanently(true);
    try {
      // Group by type
      const phonemeIds = itemsToDelete
        .filter((item) => item.itemType === "phoneme")
        .map((item) => item.id.split(":")[1])
        .filter((id): id is string => id !== undefined);

      const audioIds = itemsToDelete
        .filter((item) => item.itemType === "audio")
        .map((item) => item.id.split(":")[1])
        .filter((id): id is string => id !== undefined);

      // delete phonemes
      if (phonemeIds.length > 0) {
        if (phonemeIds.length === 1) {
          const phonemeId = phonemeIds[0];
          if (phonemeId) {
            await permanentlyDeletePhoneme({ variables: { id: phonemeId } });
          }
        } else {
          await bulkPermanentlyDeletePhonemes({ variables: { ids: phonemeIds } });
        }
      }

      // delete audio files
      if (audioIds.length > 0) {
        if (audioIds.length === 1) {
          const audioId = audioIds[0];
          if (audioId) {
            await permanentlyDeletePhonicsAudio({ variables: { id: audioId } });
          }
        } else {
          await bulkPermanentlyDeletePhonicsAudio({ variables: { ids: audioIds } });
        }
      }

      setSelectedItems(new Set());
    } catch (_error) {
      // Error already handled by mutations
    } finally {
      setIsDeletingPermanently(false);
      setShowDeleteConfirm(false);
      setItemsToDelete([]);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedItems.size === 0) return;

    try {
      // group selected items by type
      const selectedTrashItems = trashItems.filter((item) => selectedItems.has(item.id));

      const phonemeIds = selectedTrashItems
        .filter((item) => item.itemType === "phoneme")
        .map((item) => item.id.split(":")[1])
        .filter((id): id is string => id !== undefined);

      const audioIds = selectedTrashItems
        .filter((item) => item.itemType === "audio")
        .map((item) => item.id.split(":")[1])
        .filter((id): id is string => id !== undefined);

      // restore phonemes
      if (phonemeIds.length > 0) {
        await bulkRestorePhonemes({ variables: { ids: phonemeIds } });
      }

      // restore audio files
      if (audioIds.length > 0) {
        await bulkRestorePhonicsAudio({ variables: { ids: audioIds } });
      }

      setSelectedItems(new Set());
    } catch (_error) {
      // error already handled by mutations
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;

    const selectedTrashItems = trashItems.filter((item) => selectedItems.has(item.id));
    setItemsToDelete(selectedTrashItems);
    setShowDeleteConfirm(true);
  };

  const formatDate = (timestamp: number | null | undefined) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header with back button and bulk actions */}
      <TrashHeader
        title="Phonics Trash"
        itemCount={trashItems.length}
        selectedCount={selectedItems.size}
        backLabel="Back to Phonics"
        onBack={() => navigate({ to: "/media/phonics" })}
        onBulkRestore={handleBulkRestore}
        onBulkDelete={handleBulkDelete}
        isProcessing={loading}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        {/* Empty State */}
        {!loading && trashItems.length === 0 && (
          <EmptyState
            icon={FiTrash2}
            title="Trash is empty"
            description="No deleted phonemes or audio files"
          />
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading trash...</div>
          </div>
        )}

        {/* Table */}
        {!loading && trashItems.length > 0 && (
          <TrashTable
            items={trashItems}
            columns={columns}
            selectedItems={selectedItems}
            getItemId={(item) => item.id}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onRestore={handleRestore}
            onDelete={handlePermanentDelete}
            isProcessing={loading}
            renderRow={(item) => (
              <>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {item.itemType === "phoneme" ? (
                      <>
                        <FiFileText className="h-5 w-5 text-blue-500 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Phoneme</span>
                      </>
                    ) : (
                      <>
                        <FiVolume2 className="h-5 w-5 text-purple-500 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Audio File</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{item.phoneme}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {PHONEME_TYPE_LABELS[item.phonemeType] || item.phonemeType}
                </td>
                <td className="px-6 py-4">
                  {item.itemType === "phoneme" ? (
                    item.tags && item.tags.length > 0 ? (
                      <div className="text-sm text-gray-500">{item.tags.join(", ")}</div>
                    ) : (
                      <div className="text-sm text-gray-400">—</div>
                    )
                  ) : (
                    <div>
                      <div className="text-sm text-gray-900">Voice {item.voiceVariant}</div>
                      <div className="text-xs text-gray-500">{item.filename}</div>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(item.deletedAt)}
                </td>
              </>
            )}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setItemsToDelete([]);
        }}
        onConfirm={handleConfirmPermanentDelete}
        title="Permanently Delete"
        message={
          itemsToDelete.length === 1
            ? `Permanently delete this ${itemsToDelete[0]?.itemType === "phoneme" ? "phoneme (including all audio files)" : "audio file"}? This action cannot be undone.`
            : `Permanently delete ${itemsToDelete.length} items? This action cannot be undone, and all data will be removed from storage.`
        }
        confirmLabel="Delete Permanently"
        variant="danger"
        isLoading={isDeletingPermanently}
      />
    </div>
  );
}
